import { useIntersectionObserver } from 'src/common/hooks/useIntersectionObserver'
import { useAnalytics } from 'src/common/analytics'
import Section from 'src/components/shared/Section'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import useDeviceDetect from 'src/common/hooks/useDeviceDetect'
import { useRouter } from 'next/router'
import Img from 'src/components/shared/Img'

const SlickSlider = dynamic(() =>
  import('src/components/shared/BrandPages/SlickSlider')
)

export const onViewH2Id = 'promise-section'

const PromiseSection = () => {
  const router = useRouter()
  const { track } = useAnalytics()
  const { isMobile } = useDeviceDetect()
  const { ref } = useIntersectionObserver({
    threshold: 0.8,
    freezeOnceVisible: true,
    onChange: (isIntersecting) => {
      if (!isIntersecting) return
      track(
        { additional_info: { section: onViewH2Id }, event_type: 'track' },
        {},
        'section_view'
      )
    },
  })

  const onClick = (e) => {
    e.preventDefault()

    const data = {
      click_id: 'Why choose Freedom Debt Relief ',
      click_text: 'FDR-Web | Why choose Freedom Debt Relief',
      click_type: 'Button Click',
      click_url: '/why-were-better/',
      event_action: 'click',
      event_type: 'track',
    }

    track?.(data, {}, 'button_click')

    router.push('/why-were-better/')
  }

  return (
    <Section>
      <div
        className="flex w-full flex-col items-center gap-8 font-ultramarine text-gray-555 lg:gap-12"
        data-testid="facts-promise-content"
      >
        <div
          className="flex flex-col items-center gap-4"
          data-testid="facts-promise-heading"
        >
          <p
            className="text-sm font-bold uppercase tracking-[0.2em] text-[#42546B]"
            data-testid="facts-promise-eyebrow"
          >
            Why Freedom Debt Relief
          </p>
          <h2
            ref={ref}
            className="text-center text-heading-3 font-bold leading-[31.2px] text-gray-555 lg:text-left lg:text-heading-2 lg:leading-[41.6px]"
            data-testid="facts-promise-title"
          >
            Benefits you can count on
          </h2>
        </div>
        <div className="relative mb-3 w-full" data-testid="facts-promise-list">
          <SlickSlider
            sliderSettings={{
              dots: true,
              infinite: true,
              autoplay: true,
              speed: 700,
              autoplaySpeed: 12000,
              adaptiveHeight: false,
              arrows: !isMobile,
            }}
            name="promises"
          >
            {PROMISES.map((item, i) => (
              <div
                key={item.imageURL}
                className="mb-6 flex flex-col items-center gap-4 lg:mb-10"
                data-testid="facts-promise-item"
              >
                <div data-testid="facts-promise-item-image">
                  <Img
                    src={item.imageURL}
                    alt={item.title}
                    width={416}
                    height={336}
                    className="h-62 lg:w-104 w-[335px] object-contain lg:h-84"
                    isPngAvailable
                  />
                </div>
                <div
                  className="flex gap-4"
                  data-testid="facts-promise-item-copy"
                >
                  <div className="block lg:hidden">
                    <Number index={i} />
                  </div>
                  <div className="flex select-text flex-col items-start gap-2 lg:items-center lg:gap-4">
                    <span
                      className="flex items-center gap-4 text-2xl font-medium lg:text-lg"
                      data-testid="facts-promise-item-title"
                    >
                      <div className="hidden lg:block">
                        <Number index={i} />
                      </div>
                      {item.title}
                    </span>
                    <p
                      className="max-w-[639px] text-left text-14 leading-[23.8px] lg:text-center lg:text-body-medium lg:leading-[27.2px]"
                      data-testid="facts-promise-item-description"
                    >
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </SlickSlider>
        </div>
        <Link
          href={'/why-were-better/'}
          className="w-full lg:w-fit"
          prefetch={false}
          onClick={onClick}
          data-testid="facts-promise-cta-link"
        >
          <button
            className="w-full whitespace-nowrap rounded-lg bg-blue-585 px-6 py-3 text-lg font-bold text-white lg:w-fit"
            data-testid="facts-promise-cta-button"
          >
            Why choose Freedom Debt Relief
          </button>
        </Link>
      </div>
    </Section>
  )
}

export default PromiseSection

const PROMISES = [
  {
    imageURL: '/next-assets/facts/promises/promise-1.png',
    imageAlt:
      'Illustration of a man sitting on a large credit card while speaking on the phone.',
    title: 'Free debt evaluation',
    description:
      'We guide you through a personal financial assessment at no charge and no risk. We’ll help you compare your options for handling debt, so you can choose the solution that meets your needs. You decide whether debt relief is right for you.',
  },
  {
    imageURL: '/next-assets/facts/promises/promise-2.png',
    imageAlt:
      'Image of two men shaking hands with an illustration of a bar graph.',
    title: 'No upfront fees',
    description:
      'We don’t get paid until you get results. We only earn our fees once we’ve negotiated a settlement for you, you’ve approved the terms, and your creditor has been paid.',
  },
  {
    imageURL: '/next-assets/facts/promises/promise-3.png',
    imageAlt: 'Image of a woman in a meditative state.',
    title: 'Legal support included',
    description:
      'We are the only debt relief company to provide legal support for debt negotiations through our legal partner network—included in your debt relief program. Other companies charge you extra for this service.',
  },
  {
    imageURL: '/next-assets/facts/promises/promise-4.png',
    imageAlt: 'Image of a man holding a check.',
    title: 'Results guaranteed',
    description:
      'We stand by our results. If your total program settlement cost is more than the total amount of debt you enroll in our program, we’ll refund you the difference from our collected fees, up to 100%.**',
  },
]

const Number = ({ index = 0 }) => {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M17.4237 39.2867L6.09225 35.0895C4.38623 34.4594 3.10671 33.0248 2.67051 31.2703L0.15994 21.1505C-0.0533133 20.2781 -0.0533133 19.3669 0.15994 18.5042L2.6802 8.38436C3.1164 6.62018 4.39592 5.19526 6.10195 4.56519L17.4237 0.358291C18.6548 -0.097295 20.0022 -0.097295 21.2332 0.358291L32.5647 4.5555C34.2707 5.18557 35.5503 6.62018 35.9865 8.37467L38.5067 18.4945C38.72 19.3669 38.72 20.2781 38.5067 21.1408L35.9865 31.2606C35.5503 33.0248 34.2707 34.4497 32.5647 35.0798L21.2332 39.277C20.0022 39.7326 18.6548 39.7326 17.4237 39.277V39.2867Z"
        fill="#25438E"
      />
      {index === 0 && (
        <path
          d="M18.2919 26.7995V16.4245H15.9336V12.3662H22.8169V26.7995H18.2919Z"
          fill="white"
        />
      )}
      {index === 1 && (
        <path
          d="M18.7341 18.9087C19.9508 17.9587 20.4174 17.517 20.4174 16.842C20.4174 16.167 19.9508 15.7253 19.3008 15.7253C18.5674 15.7253 18.1008 16.2753 18.0758 17.2837H13.6758C13.6758 13.7087 15.8674 11.542 19.2924 11.542C22.7174 11.542 24.9091 13.6253 24.9091 16.5503C24.9091 18.4837 24.0008 19.942 21.3091 21.792C20.8841 22.0837 20.5924 22.2587 20.3841 22.3587H24.9758V26.292H13.7174V22.7587L18.7258 18.9087H18.7341Z"
          fill="white"
        />
      )}
      {index === 2 && (
        <path
          d="M20.0828 16.8253C20.0828 16.342 19.7245 15.9003 19.0745 15.9003C18.3828 15.9003 18.0411 16.492 18.0245 17.1587H13.8995C13.8995 13.942 16.0411 12.042 19.2411 12.042C22.2495 12.042 24.4828 13.8087 24.4578 16.192C24.4578 17.4503 23.9495 18.3837 22.8995 19.092C24.2911 19.7837 25.0245 20.817 25.0245 22.3753C25.0245 25.1753 22.6495 27.1087 19.3161 27.1087C15.9828 27.1087 13.6328 25.1753 13.6328 21.6337H18.0078C18.0495 22.5587 18.5328 23.1253 19.2745 23.1253C20.0161 23.1253 20.5411 22.7087 20.5411 22.0337C20.5411 21.3587 20.0161 20.9587 19.1495 20.9587H18.4995V17.8003H18.9828C19.6328 17.8003 20.0745 17.3837 20.0745 16.8087L20.0828 16.8253Z"
          fill="white"
        />
      )}
      {index === 3 && (
        <path
          d="M12.1656 23.2318V20.3336L17.2789 11.4941H22.7234V19.6712H24.7107V23.2318H22.7234V25.6953H18.6038V23.2318H12.1656ZM16.6785 19.6712H18.6452V15.7379L16.6785 19.6712Z"
          fill="white"
        />
      )}
    </svg>
  )
}
